import { useChangePasswordMutation } from "@/api/auth/useAuthAPI";
import { CloseIcon } from "@/assets/icons";
import NotificationContent from "@/components/notification/notificationContent";
import Modal from "@/components/ui/modal";
import { MESSAGE } from "@/constants/message";
import useToast, { EToastType } from "@/hooks/ui/useToast";

import { ChangePasswordForm } from "./ChangePasswordForm";
import { ERROR_DATA_MAPPING } from "./data";

const getErrorData = (code: number) =>
  ERROR_DATA_MAPPING[code as keyof typeof ERROR_DATA_MAPPING] ??
  ERROR_DATA_MAPPING[500];

export const ChangePasswordPanel = ({
  showChangePasswordPanel,
  setShowChangePasswordPanel
}: {
  showChangePasswordPanel: boolean;
  setShowChangePasswordPanel: (params: boolean) => void;
}) => {
  const { showToast } = useToast();
  const { mutate: changePassword, isPending } = useChangePasswordMutation({
    onSuccess: () => {
      setShowChangePasswordPanel(false);
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <NotificationContent
            title={MESSAGE.CHANGE_PASSWORD_SUCCESS_TITLE}
            description={MESSAGE.CHANGE_PASSWORD_SUCCESS_DESCRIPTION}
          />
        ),
        autoClose: 3000
      });
    },
    onError: (error) => {
      const errorCode = error?.code || 500;
      showToast({
        type: EToastType.ERROR,
        message: (
          <NotificationContent
            title={getErrorData(errorCode).title}
            description={getErrorData(errorCode).description}
          />
        ),
        autoClose: 3000
      });
    }
  });

  return (
    <Modal
      shouldCloseModalOnClickOutside={false}
      isOpen={showChangePasswordPanel}
      onClose={() => setShowChangePasswordPanel(false)}
      containerClassname="!static"
    >
      <div className="absolute top-[16px] right-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[456px] h-[calc(100vh-32px)] overflow-y-auto flex flex-col p-6 gap-6">
        <section className="flex items-center justify-between gap-4">
          <h2 className="text-2xl font-semibold text-txtHeading">
            Change Password
          </h2>
          <button
            onClick={() => setShowChangePasswordPanel(false)}
            className="bg-transparent border-0 p-0"
            aria-label="Close"
            data-testid="change-password-panel-close-btn"
          >
            <CloseIcon className="fill-dim-gray" />
          </button>
        </section>
        <ChangePasswordForm
          isSubmitting={isPending}
          onSubmit={({ currentPassword, newPassword }) => {
            changePassword({
              currentPassword,
              newPassword
            });
          }}
          onClose={() => {
            setShowChangePasswordPanel(false);
          }}
        />
      </div>
    </Modal>
  );
};

export default ChangePasswordPanel;
